import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector, { type CustomDetector } from 'i18next-browser-languagedetector';

import { DEFAULT_LANG, SUPPORTED_CODES, resolveLanguage } from './languages';
import { fetchStoredLanguage, syncTelegramLanguage } from '../ton/notify';

import en from './locales/en.json';
import ru from './locales/ru.json';
import es from './locales/es.json';
import pt from './locales/pt.json';
import fr from './locales/fr.json';
import de from './locales/de.json';
import tr from './locales/tr.json';
import id from './locales/id.json';
import uk from './locales/uk.json';
import hi from './locales/hi.json';
import zh from './locales/zh.json';
import ar from './locales/ar.json';

export const resources = {
  en: { translation: en },
  ru: { translation: ru },
  es: { translation: es },
  pt: { translation: pt },
  fr: { translation: fr },
  de: { translation: de },
  tr: { translation: tr },
  id: { translation: id },
  uk: { translation: uk },
  hi: { translation: hi },
  zh: { translation: zh },
  ar: { translation: ar },
} as const;

/**
 * Telegram Mini Apps expose the user's interface language via
 * `Telegram.WebApp.initDataUnsafe.user.language_code`. Detect it first so the
 * mini-app opens in the same language as the user's Telegram client, before
 * falling back to the browser locale.
 */
const telegramDetector: CustomDetector = {
  name: 'telegram',
  lookup() {
    const code = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
    return code || undefined;
  },
};

const detector = new LanguageDetector();
detector.addDetector(telegramDetector);

void i18n
  .use(detector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: SUPPORTED_CODES,
    fallbackLng: DEFAULT_LANG,
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    detection: {
      order: ['telegram', 'localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'pyramida_lang',
    },
  });

/** Apply `<html lang dir>` for the active language (RTL-aware). */
export function applyDocumentLanguage(lng: string): void {
  const def = resolveLanguage(lng);
  document.documentElement.lang = def.code;
  document.documentElement.dir = def.dir;
}

i18n.on('languageChanged', applyDocumentLanguage);
applyDocumentLanguage(i18n.language);

// Persist the active language to the notification bot (inside Telegram only)
// whenever the user switches it, so the choice propagates to the bot / AI bot.
// Best-effort and de-duplicated.
i18n.on('languageChanged', (lng) => {
  void syncTelegramLanguage(lng);
});

// Cross-surface language sync: the bot's stored `lang` (set in the bot, the AI
// recruiter bot, or a previous app session) is the source of truth. On startup,
// adopt it if present — so the app opens in the language chosen elsewhere —
// otherwise seed the store with the detected language. Any later user switch is
// pushed back by the `languageChanged` handler above, keeping all surfaces in step.
void (async () => {
  const stored = await fetchStoredLanguage();
  const resolved = stored ? resolveLanguage(stored).code : null;
  if (resolved && resolved !== i18n.language) {
    await i18n.changeLanguage(resolved); // triggers languageChanged -> sync + <html lang>
  } else {
    void syncTelegramLanguage(i18n.language);
  }
})();

/** BCP-47 locale tag for `Intl` formatters, derived from the active language. */
export function activeLocale(): string {
  return resolveLanguage(i18n.language).locale;
}

export default i18n;
